import { ComponentInUseIcon, ComponentIssueIcon, SelectProductIcon, TotalProjectsIcon, RequestRecordIcon } from "@components/Icons";
import AnnouncementsModal from "@components/UI/AnnouncementsModal";
import DashboardProductSelect, {
  getEffectiveSelection,
  isAllSelectedInTab,
} from "@components/Dashboard/DashboardProductSelect";
import { CustomDatePicker } from "@components/Forms";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { LOCAL_STORAGE } from "@constants/index";
import { useDashboard } from "@hooks/useDashboard";
import useDebounce from "@hooks/useDebounce";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDate, truncateText } from "@utils/common";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
);

const VERIFICATION_CHART_MD_MIN_WIDTH = "(min-width: 1290px)";

const MANAGED_PRODUCT_ROLES = new Set(["PRODUCT_MANAGER"]);
const PARTICIPATE_PRODUCT_ROLES = new Set(["PROJECT_MANAGER", "MEMBER"]);

const extractMyProductsWithRoleFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.object)) return response.object;
  return [];
};

const mapMyProductsWithRoleToOptions = (items) =>
  (items ?? []).map((item) => ({
    value: item.productSeq,
    label: item.productName,
    role: item.role,
  }));

const splitProductsByRole = (products) => {
  const managedOptions = [];
  const participateOptions = [];

  products.forEach((product) => {
    if (MANAGED_PRODUCT_ROLES.has(product.role)) {
      managedOptions.push(product);
      return;
    }
    if (PARTICIPATE_PRODUCT_ROLES.has(product.role)) {
      participateOptions.push(product);
    }
  });

  return { managedOptions, participateOptions };
};

/** Compact bars + spacing on small screens only; large screens use legacy barThickness 40. */
function getVerificationBarLayout(categoryCount, isSmallScreen) {
  if (!isSmallScreen) return null;
  if (categoryCount >= 4) return { maxBarThickness: 30, categoryPercentage: 0.88, barPercentage: 0.75 };
  if (categoryCount === 3) return { maxBarThickness: 18, categoryPercentage: 0.42, barPercentage: 0.8 };
  if (categoryCount === 2) return { maxBarThickness: 22, categoryPercentage: 0.48, barPercentage: 0.85 };
  return { maxBarThickness: 28, categoryPercentage: 0.55, barPercentage: 0.88 };
}

function Dashboard() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  // State for filters
  const [managedSelection, setManagedSelection] = useState(null);
  const [participateSelection, setParticipateSelection] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcementsData, setAnnouncementsData] = useState([]);
  const [myProductsWithRoleList, setMyProductsWithRoleList] = useState([]);
  const { scanToolList, fetchSearchInfo } = useSearchInfo();
  const { fetchDashboardData } = useDashboard();
  const [requestData, setRequestData] = useState({});
  const [statusData, setStatusData] = useState({});
  const [vulnerabilityData, setVulnerabilityData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // System info and JVM metrics state
  const [systemInfo, setSystemInfo] = useState({
    jvmMemory: { used: 0, max: 0, available: 0, percentage: 0 },
    memoryUsage: { percentage: 0 },
    cpuUsage: { percentage: 0 },
    diskspace: { percentage: 0 },
  });
  const [isLoadingSystemInfo, setIsLoadingSystemInfo] = useState(false);
  const [scanToolHealth, setScanToolHealth] = useState({
    fossid: { status: "UNKNOWN" },
    clarity: { status: "UNKNOWN" },
    clarityair: { status: "UNKNOWN" },
    checkedAt: null,
  });
  const [isLoadingScanTool, setIsLoadingScanTool] = useState(false);

  const [verificationChartSmallScreen, setVerificationChartSmallScreen] = useState(() =>
    typeof window !== "undefined"
      ? !window.matchMedia(VERIFICATION_CHART_MD_MIN_WIDTH).matches
      : false,
  );

  // Ref to track if this is the initial mount
  const isInitialMount = useRef(true);
  const hasInitializedProductSelection = useRef(false);

  const getMyProductsWithRole = useMutationData(
    queryKeys.myProductsWithRole,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setMyProductsWithRoleList(
            mapMyProductsWithRoleToOptions(extractMyProductsWithRoleFromResponse(response)),
          );
        }
      },
      onError: (error) => {
        console.error("Error fetching my products with role:", error);
      },
    },
  );

  // Mutation for fetching bulletin list for popup
  const getAnnouncements = useMutationData(
    queryKeys.getBulletinListForPopup,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && response.length > 0) {
          setAnnouncementsData(response);
          setShowAnnouncementsModal(true);
        }
        // Clear the login flag regardless of result
        localStorage.removeItem(LOCAL_STORAGE.JUST_LOGGED_IN);
      },
      onError: (error) => {
        console.error("Error fetching bulletin list:", error);
        // Clear the login flag on error
        localStorage.removeItem(LOCAL_STORAGE.JUST_LOGGED_IN);
      },
    },
  );

  useEffect(() => {
    fetchSearchInfo();
    getMyProductsWithRole.mutateAsync();
    fetchDashboardDataWithParams();
    fetchSystemInfo();
    fetchScanToolHealth();

    const justLoggedIn = localStorage.getItem(LOCAL_STORAGE.JUST_LOGGED_IN);
    if (justLoggedIn === "true") {
      getAnnouncements.mutateAsync();
    }
  }, []);

  const { managedOptions, participateOptions } = useMemo(
    () => splitProductsByRole(myProductsWithRoleList),
    [myProductsWithRoleList],
  );

  const managedSelected = useMemo(
    () => getEffectiveSelection(managedOptions, managedSelection),
    [managedOptions, managedSelection],
  );
  const participateSelected = useMemo(
    () => getEffectiveSelection(participateOptions, participateSelection),
    [participateOptions, participateSelection],
  );
  const combinedSelectedProducts = useMemo(
    () => [...managedSelected, ...participateSelected],
    [managedSelected, participateSelected],
  );

  const isAllProductsSelected = useMemo(() => {
    const totalCount = managedOptions.length + participateOptions.length;
    if (totalCount === 0) return false;
    return (
      isAllSelectedInTab(managedOptions, managedSelection) &&
      isAllSelectedInTab(participateOptions, participateSelection)
    );
  }, [managedOptions, participateOptions, managedSelection, participateSelection]);

  const productSelectionKey = useMemo(
    () => combinedSelectedProducts.map((product) => product.value).sort().join(","),
    [combinedSelectedProducts],
  );
  const debouncedProductSelectionKey = useDebounce(productSelectionKey, 500);

  // Initialize default: both tabs select all once my products are loaded.
  useEffect(() => {
    if (hasInitializedProductSelection.current) return;
    if (!myProductsWithRoleList || myProductsWithRoleList.length === 0) return;

    hasInitializedProductSelection.current = true;
    setManagedSelection(null);
    setParticipateSelection(null);
  }, [myProductsWithRoleList]);

  // Auto-refresh system info every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSystemInfo();
      fetchScanToolHealth();
    }, 600000); // 600 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(VERIFICATION_CHART_MD_MIN_WIDTH);
    const update = () => setVerificationChartSmallScreen(!mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  // Mutations for system info
  const jvmMetricsMutation = useMutationData(
    "/jvm-metrics",
    { method: "GET" },
    {
      onError: (error) => console.error("Error fetching JVM metrics:", error),
    },
  );

  const hostMetricsMutation = useMutationData(
    "/host-metrics",
    { method: "GET" },
    {
      onError: (error) => console.error("Error fetching host metrics:", error),
    },
  );

  const scanToolHealthMutation = useMutationData(
    "/scan-tools/health",
    { method: "GET" },
    {
      onError: (error) => console.error("Error fetching scan tool health:", error),
    },
  );

  const getStatusColor = (status) => {
    if (status === "Healthy") return colors.bgStatusHealthy;
    if (status === "Unhealthy") return colors.bgStatusUnhealthy;
    if (status === "Unconfigured") return colors.bgStatusUnconfigured;
    return colors.bgStatusUnknown;
  };

  const getStatusTextColor = (status) => {
    if (status === "Healthy") return colors.textStatusHealthy;
    if (status === "Unhealthy") return colors.textStatusUnhealthy;
    if (status === "Unconfigured") return colors.textStatusUnconfigured;
    return colors.textStatusUnknown;
  };

  const getStatusLabel = (item) => {
    if (!item || !item.status) return t("dashboard.scan-tool-unknown");
    
    // Map status to translation keys
    const statusMap = {
      "Healthy": t("dashboard.scan-tool-healthy"),
      "Unhealthy": t("dashboard.scan-tool-unhealthy"),
      "Unconfigured": t("dashboard.scan-tool-unconfigured"),
      "UNKNOWN": t("dashboard.scan-tool-unknown")
    };
    
    return statusMap[item.status] || item.status;
  };

  // Check if FossID is enabled based on scanToolList
  const isFossIdEnabled = () => {
    return scanToolList && scanToolList.some(tool => tool.value === "fossid");
  };

  const isClarityAirEnabled = () => {
    return scanToolList && scanToolList.some(tool => tool.value === "clarityair");
  };


  // Format bytes to human-readable string (1 decimal)
  const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return null;
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
  };

  const formatUsageLabel = (used, total) => {
    const usedStr = formatBytes(used);
    const totalStr = formatBytes(total);
    if (!usedStr || !totalStr) return null;
    return `${usedStr} / ${totalStr}`;
  };

  // Function to fetch system info (JVM metrics + host metrics)
  const fetchSystemInfo = useCallback(async () => {
    try {
      setIsLoadingSystemInfo(true);

      // Fetch in parallel but tolerate one side failing
      const [jvmResult, hostResult] = await Promise.allSettled([
        jvmMetricsMutation.mutateAsync(),
        hostMetricsMutation.mutateAsync(),
      ]);

      const jvmMetrics =
        jvmResult.status === "fulfilled" ? jvmResult.value : { jvmMemory: {} };
      const hostMetrics =
        hostResult.status === "fulfilled" ? hostResult.value : {};

      const jvmMemory = jvmMetrics?.jvmMemory || {};
      const jvmAvailablePct =
        jvmMemory.max > 0 ? ((jvmMemory.available ?? 0) / jvmMemory.max) * 100 : 0;
      const memoryPercentage = hostMetrics?.memory?.usage_percent || 0;
      const memoryUsed = hostMetrics?.memory?.used ?? hostMetrics?.memory?.used_bytes ?? 0;
      const memoryTotal = hostMetrics?.memory?.total ?? hostMetrics?.memory?.total_bytes ?? 0;
      const cpuPercentage = hostMetrics?.cpu?.usage_percent || 0;
      const diskPercentage = hostMetrics?.disk?.usage_percent || 0;
      const diskUsed = hostMetrics?.disk?.used ?? hostMetrics?.disk?.used_bytes ?? 0;
      const diskTotal = hostMetrics?.disk?.total ?? hostMetrics?.disk?.total_bytes ?? 0;

      setSystemInfo({
        jvmMemory: {
          used: jvmMemory.used || 0,
          max: jvmMemory.max || 0,
          available: jvmMemory.available || 0,
          percentage: parseFloat(jvmAvailablePct || 0),
        },
        memoryUsage: {
          percentage: parseFloat(memoryPercentage),
          used: memoryUsed,
          total: memoryTotal,
        },
        cpuUsage: {
          percentage: parseFloat(cpuPercentage),
        },
        diskspace: {
          percentage: parseFloat(diskPercentage),
          used: diskUsed,
          total: diskTotal,
        },
      });
    } catch (error) {
      console.error("Error fetching system info:", error);
      // Set default values on error
      setSystemInfo({
        jvmMemory: { used: 0, max: 0, available: 0, percentage: 0 },
        memoryUsage: { percentage: 0, used: 0, total: 0 },
        cpuUsage: { percentage: 0 },
        diskspace: { percentage: 0, used: 0, total: 0 },
      });
    } finally {
      setIsLoadingSystemInfo(false);
    }
  }, [jvmMetricsMutation, hostMetricsMutation]);

  const fetchScanToolHealth = useCallback(async () => {
    try {
      setIsLoadingScanTool(true);
      const res = await scanToolHealthMutation.mutateAsync();
      setScanToolHealth({
        fossid: res?.fossid || { status: "UNKNOWN" },
        clarityair: res?.clarityAir || { status: "UNKNOWN" },
        clarity: res?.clarity || { status: "UNKNOWN" },
        checkedAt: res?.checkedAt || null,
      });
    } catch (error) {
      console.error("Error fetching scan tool health:", error);
      setScanToolHealth({
        fossid: { status: "UNHEALTHY", error: error?.message },
        clarityair: { status: "UNHEALTHY", error: error?.message },
        clarity: { status: "UNHEALTHY", error: error?.message },
        checkedAt: null,
      });
    } finally {
      setIsLoadingScanTool(false);
    }
  }, [scanToolHealthMutation]);

  // Effect to handle custom date range changes and refetch data
  useEffect(() => {
    // Skip the initial call since it's handled in the first useEffect
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Set date filter when custom date range is selected
    if (customDateRange.startDate && customDateRange.endDate) {
      setDateFilter("custom");
    }

    // Always fetch data when product selection changes, regardless of value
    // Also fetch when date range changes
    if (customDateRange.startDate || customDateRange.endDate || debouncedProductSelectionKey !== undefined) {
      fetchDashboardDataWithParams();
    }
  }, [customDateRange.startDate, customDateRange.endDate, debouncedProductSelectionKey]);

  // Function to fetch dashboard data with parameters
  const fetchDashboardDataWithParams = useCallback(async () => {
    try {
      setIsLoading(true);
      setRequestData({});
      // Semantics:
      // - null/undefined: ALL products (default)
      // - []: NONE selected => show 0 data (do not call APIs)
      // - [..]: only those selected
      const isNoneSelected = combinedSelectedProducts.length === 0;
      if (isNoneSelected) {
        setRequestData({ pendingDone: 0, pendingApproval: 0, pendingCompletion: 0, requests: [] });
        // Ensure charts always receive valid arrays to prevent Chart.js legend crashes.
        setStatusData({
          totalProjects: 0,
          componentsInUse: 0,
          componentsIssue: 0,
          clarityDone: 0,
          clarityProcessing: 0,
          sourceCodeDone: 0,
          sourceCodeProcessing: 0,
          sbomDone: 0,
          sbomProcessing: 0,
          clarityAirDone: 0,
          clarityAirProcessing: 0,
          topComponentsUsage: [],
          topLicensesUsage: [],
          componentsIssue6Months: [],
        });
        setVulnerabilityData({
          totalVulnerabilityLevelCountS: 0,
          totalVulnerabilityLevelCountA: 0,
          totalVulnerabilityLevelCountB: 0,
          totalVulnerabilityLevelCountC: 0,
          totalObligationType1Count: 0,
          totalObligationType2Count: 0,
          totalObligationType3Count: 0,
          totalObligationType4Count: 0,
          totalObligationType5Count: 0,
          totalLicenseConflictBetweenComponentsCount: 0,
          totalPolicyConflictCount: 0,
        });
        setIsLoading(false);
        return;
      }

      const productSeqs = isAllProductsSelected
        ? null
        : combinedSelectedProducts.map((p) => parseInt(p.value, 10));

      const dateFrom = customDateRange.startDate ? formatDate(customDateRange.startDate.toISOString()) : null;
      const dateTo = customDateRange.endDate ? formatDate(customDateRange.endDate.toISOString()) : null;

      const res = await fetchDashboardData(productSeqs, dateFrom, dateTo);
      setRequestData(res.requestsData);
      setStatusData(res.statusData);
      setVulnerabilityData(res.vulnerabilityData);
    } catch (error) {
      console.error("Error fetching dashboard data with params:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    combinedSelectedProducts,
    isAllProductsSelected,
    customDateRange.startDate,
    customDateRange.endDate,
    fetchDashboardData,
  ]);

  // Process components issue data for the last 6 months
  const processComponentsIssueData = (componentsIssue6Months) => {
    if (!componentsIssue6Months || !Array.isArray(componentsIssue6Months)) {
      return { months: [], issues: [] };
    }

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const currentYear = currentDate.getFullYear();

    // Create array for last 6 months
    const last6Months = [];
    const last6MonthsData = [];

    for (let i = 5; i >= 0; i--) {
      const monthNumber = currentMonth - i;
      const adjustedMonth = monthNumber <= 0 ? monthNumber + 12 : monthNumber;

      // Calculate year for this month
      let yearForMonth = currentYear;
      if (monthNumber <= 0) {
        yearForMonth = currentYear - 1;
      }

      // Find data for this month
      const monthData = componentsIssue6Months.find((item) => item.month === adjustedMonth);
      const componentCount = monthData ? monthData.componentCount : 0;

      // Add month label with year and data
      last6Months.push(`${monthNames[adjustedMonth - 1]} ${yearForMonth}`);
      last6MonthsData.push(componentCount);
    }

    return {
      months: last6Months,
      issues: last6MonthsData,
    };
  };

  // Process the API data for components issue chart
  const processedComponentsIssueData = processComponentsIssueData(statusData.componentsIssue6Months);

  const totalSelectedProducts = combinedSelectedProducts.length;

  // Toggle state for components/licenses
  const [showComponents, setShowComponents] = useState(true);

  // Format completion date with tags
  const formatCompletionDate = (dateString) => {
    if (!dateString) return { text: t("dashboard.no-date"), color: colors.dashboardDateMuted };

    const today = new Date();
    const completionDate = new Date(dateString);

    // Check if date is valid
    if (isNaN(completionDate.getTime())) {
      return { text: t("dashboard.invalid-date"), color: colors.dashboardDateInvalid };
    }

    const diffTime = completionDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return {
        text: t("dashboard.today"),
        color: colors.dashboardDateToday,
        custom: colors.dashboardDateTodayBadge,
      };
    } else if (diffDays === 1) {
      return {
        text: t("dashboard.tomorrow"),
        color: colors.dashboardDateTomorrow,
        custom: colors.dashboardDateTomorrowBadge,
      };
    } else if (diffDays === -1) {
      return {
        text: t("dashboard.yesterday"),
        color: colors.dashboardDateYesterday,
        custom: colors.dashboardDateYesterdayBadge,
      };
    } else if (diffDays < 0) {
      // All past dates (day before yesterday and earlier) - format as YYYY-MM-DD with red color
      const year = completionDate.getFullYear();
      const month = String(completionDate.getMonth() + 1).padStart(2, "0");
      const day = String(completionDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;
      return {
        text: formattedDate,
        color: colors.dashboardDateYesterday,
        custom: "px-2 py-1 text-sm font-medium",
      };
    } else {
      // Future date - format as YYYY-MM-DD
      const year = completionDate.getFullYear();
      const month = String(completionDate.getMonth() + 1).padStart(2, "0");
      const day = String(completionDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;
      return { text: formattedDate, color: colors.dashboardDateFuture, custom: "px-2 py-1 text-sm" };
    }
  };

  // Handle request row click navigation
  const handleRequestClick = async (request) => {
    try {
      const { projectSeq, projectId, verificationType, verificationSeq } = request;

      if (!projectId || !projectSeq) {
        showErrorToast(t("common.error"), t("dashboard.navigation.project-not-available"));
        return;
      }

      navigate(`/projects/${projectSeq}/${verificationType}/${verificationSeq}`, {
        state: { activeTabFromDashboard: 1 },
      });
    } catch (error) {
      if (error.response?.status === 404) {
        showErrorToast(t("common.error"), t("dashboard.navigation.project-not-available"));
      } else if (error.response?.status === 403) {
        showErrorToast(t("common.error"), t("dashboard.navigation.access-denied"));
      } else {
        showErrorToast(t("common.error"), t("dashboard.navigation.navigation-error"));
      }
    }
  };

  // Sort requests by due date
  const sortedRequests = useMemo(() => {
    if (!requestData.requests || requestData.requests.length === 0) {
      return [];
    }

    return [...requestData.requests].sort((a, b) => {
      const dateA = new Date(a.completionRequestDt);
      const dateB = new Date(b.completionRequestDt);
      const today = new Date();

      const diffA = dateA.getTime() - today.getTime();
      const diffB = dateB.getTime() - today.getTime();

      // Sort by: overdue (negative), due today (0), due tomorrow (1), future dates
      if (diffA < 0 && diffB >= 0) return -1;
      if (diffA >= 0 && diffB < 0) return 1;
      if (diffA === 0 && diffB !== 0) return -1;
      if (diffA !== 0 && diffB === 0) return 1;
      if (diffA === 1 && diffB !== 1) return -1;
      if (diffA !== 1 && diffB === 1) return -1;

      return dateA - dateB;
    });
  }, [requestData.requests]);

  // Calculate max value for chart scaling
  const maxVerificationValue = Math.max(
    (statusData.clarityDone || 0) + (statusData.clarityProcessing || 0),
    (statusData.sourceCodeDone || 0) + (statusData.sourceCodeProcessing || 0),
    (statusData.sbomDone || 0) + (statusData.sbomProcessing || 0),
    (statusData.clarityAirDone || 0) + (statusData.clarityAirProcessing || 0),
  );

  // Chart configurations
  const labelsRaw = [t("dashboard.clarity"), t("dashboard.fossid"), t("dashboard.sbom"), t("dashboard.clarityair")];
  const processingRaw = [statusData.clarityProcessing, statusData.sourceCodeProcessing, statusData.sbomProcessing, statusData.clarityAirProcessing];
  const doneRaw = [statusData.clarityDone, statusData.sourceCodeDone, statusData.sbomDone, statusData.clarityAirDone];

  // filter out 0 values
  const filteredLabels = [];
  const filteredProcessing = [];
  const filteredDone = [];

  labelsRaw.forEach((label, idx) => {
    const proc = processingRaw[idx];
    const done = doneRaw[idx];

    // Only push columns that have at least 1 non-zero value
    if (proc !== 0 || done !== 0) {
      filteredLabels.push(label);
      filteredProcessing.push(proc);
      filteredDone.push(done);
    }
  });

  const verificationBarLayout = getVerificationBarLayout(
    filteredLabels.length,
    verificationChartSmallScreen,
  );

  const verificationStatusChartData = {
    labels: filteredLabels,
    datasets: [
      {
        label: t("dashboard.processing"),
        data: filteredProcessing,
        backgroundColor: colors.chartSeriesPurple,
        borderColor: colors.chartSeriesPurple,
        borderWidth: 0,
        ...(verificationBarLayout ? {} : { barThickness: 40 }),
      },
      {
        label: t("dashboard.done"),
        data: filteredDone,
        backgroundColor: colors.chartSeriesLime,
        borderColor: colors.chartSeriesLime,
        borderWidth: 0,
        ...(verificationBarLayout ? {} : { barThickness: 40 }),
        borderRadius: {
          topLeft: 6,
          topRight: 6,
          bottomLeft: 0,
          bottomRight: 0,
        },
      },
    ],
  };

  const verificationStatusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    ...(verificationBarLayout
      ? {
          datasets: {
            bar: {
              maxBarThickness: verificationBarLayout.maxBarThickness,
              categoryPercentage: verificationBarLayout.categoryPercentage,
              barPercentage: verificationBarLayout.barPercentage,
            },
          },
        }
      : {}),
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: colors.chartLegendColor,
          font: { size: 12 },
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2,
        cornerRadius: 15, // Very rounded corners
        padding: 18, // Large padding
        // Custom fonts
        bodyFont: {
          size: 14,
          weight: "500",
        },
        titleFont: {
          size: 16,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const label = context.dataset.label;
            const value = context.parsed.y;
            return `${label}: ${value}`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            return {
              borderColor: "transparent",
              backgroundColor: context.dataset.backgroundColor,
              borderRadius: 6, // Makes it circular
            };
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          color: colors.chartTickColor,
          autoSkip: false,
          ...(verificationChartSmallScreen && {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 10 },
          }),
          callback: function (value, index, ticks) {
            const chart = this.chart;
            const datasets = chart.data.datasets;

            const total = datasets.reduce((sum, ds) => {
              const v = ds.data[index];
              return sum + (typeof v === "number" ? v : 0);
            }, 0);

            if (total === 0) return "";

            return chart.data.labels[index];
          }
        },
      },
      y: {
        stacked: true,
        grid: {
          color: colors.chartGridColor,
          lineWidth: 1,
          borderDash: [5, 5],
        },
        ticks: {
          color: colors.chartTickColor,
          stepSize: (() => {
            if (!maxVerificationValue || isNaN(maxVerificationValue) || maxVerificationValue === 0) return 1;
            if (maxVerificationValue <= 10) return 1;
            if (maxVerificationValue <= 25) return 5;
            return undefined; // Auto-calculate for > 25
          })(),
          callback: function (value) {
            return value;
          },
        },
        min: 0,
        max: (() => {
          if (!maxVerificationValue || isNaN(maxVerificationValue) || maxVerificationValue === 0) return 0;
          if (maxVerificationValue <= 10) return 10;
          if (maxVerificationValue <= 25) return 25;
          return maxVerificationValue; // Dynamic for > 25
        })(),
        beginAtZero: true,
      },
    },
  };

  const hasVerificationStatusData =
    verificationStatusChartData?.labels?.length > 0 &&
    verificationStatusChartData?.datasets?.some(
      (ds) =>
        Array.isArray(ds.data) &&
        ds.data.some((v) => typeof v === "number" && v > 0)
    );

  const requestsChartData = {
    labels: [t("dashboard.done"), t("dashboard.pending-approval"), t("dashboard.pending-completion")],
    datasets: [
      {
        data: [requestData.pendingDone, requestData.pendingApproval, requestData.pendingCompletion],
        backgroundColor: [colors.chartSeriesLime, colors.chartSeriesPink, colors.chartSeriesPurple],
        borderWidth: 0,
        cutout: "60%",
        hoverOffset: 3,
        borderRadius: 8,
        spacing: 3,
      },
    ],
  };

  // Replace your existing requestsChartOptions with this fixed version
  const requestsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: "nearest",
        xAlign: "center",
        yAlign: "top",
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 1,
        cornerRadius: 8, // Makes corners more rounded (default is 4)
        padding: 12, // Increases padding for more width/height
        displayColors: true, // Keep this true
        caretPadding: 20,
        // Custom width and height
        bodyFont: {
          size: 14, // Larger font size
        },
        titleFont: {
          size: 16, // Larger title font
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(0);
            // Use abbreviated labels for better readability
            let displayLabel = context.label;
            if (context.label === "Pending Completion") {
              displayLabel = "Pend. Comp.";
            } else if (context.label === "Pending Approval") {
              displayLabel = "Pend. Appr.";
            }
            return `${displayLabel}: ${value} (${percentage}%)`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            return {
              backgroundColor: context.dataset.backgroundColor[context.dataIndex],
              borderRadius: 6, // Makes it circular
            };
          },
        },
      },
    },
  };

  const componentsUsageChartData = {
    labels: showComponents
      ? statusData.topComponentsUsage?.map((c) => c.componentName)
      : statusData.topLicensesUsage?.map((l) => l.licenseName),
    datasets: [
      {
        data: showComponents
          ? statusData.topComponentsUsage?.map((c) => c.componentCount)
          : statusData.topLicensesUsage?.map((l) => l.licenseCount),
        backgroundColor: [colors.chartSeriesPink, colors.chartSeriesPurple, colors.chartSeriesYellow, colors.chartSeriesLime, colors.chartSeriesCyan],
        borderWidth: 0,
        cutout: "60%",
        hoverOffset: 3,
        borderRadius: 8,
        spacing: 6,
      },
    ],
  };

  const componentsUsageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: "nearest",
        xAlign: "center",
        yAlign: "bottom",
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2, // Thicker border
        cornerRadius: 12, // More rounded corners
        padding: 16, // More padding for larger size
        displayColors: true, // Keep this true
        caretPadding: 25,
        // Custom dimensions
        bodyFont: {
          size: 15,
          weight: "500",
        },
        titleFont: {
          size: 17,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(0);
            return `${truncateText(context.label, 25)} : ${value} (${percentage}%)`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            return {
              backgroundColor: context.dataset.backgroundColor[context.dataIndex],
              borderRadius: 6, // Makes it circular
            };
          },
        },
      },
    },
  };

  const vulnerabilityIssuesChartData = {
    labels: processedComponentsIssueData.months,
    datasets: [
      {
        label: t("dashboard.issue-components"),
        data: processedComponentsIssueData.issues,
        borderColor: colors.chartSeriesRed,
        backgroundColor: colors.chartSeriesRedSubtle,
        borderWidth: 2,
        fill: true,
        tension: 0,
      },
    ],
  };

  const vulnerabilityIssuesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2,
        cornerRadius: 15, // Very rounded corners
        padding: 18, // Large padding
        // Custom fonts
        bodyFont: {
          size: 14,
          weight: "500",
        },
        titleFont: {
          size: 16,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const label = context.dataset.label;
            const value = context.parsed.y;
            return `${label}: ${value}`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            return {
              borderColor: "transparent",
              backgroundColor: context.dataset.backgroundColor,
              borderRadius: 6, // Makes it circular
            };
          },
        },
      },
      crosshair: {
        line: {
          color: colors.chartCrosshairColor,
          width: 1,
          dashPattern: [5, 5],
        },
        sync: {
          enabled: true,
          group: 1,
        },
        snap: {
          enabled: true,
        },
      },
    },
    scales: {
      x: {
        grid: { color: colors.chartGridColor },
        ticks: { color: colors.chartTickColor },
      },
      y: {
        grid: { color: colors.chartGridColor },
        min: Math.min(...processedComponentsIssueData.issues) ? 0 : undefined,
        max: processedComponentsIssueData.issues.every(v => v === 0) ? 0.1 : undefined,
        ticks: {
          color: colors.chartTickColor,
          callback: function(value) {
            if (Number.isInteger(value)) {
              return new Intl.NumberFormat().format(value);
            }
            return undefined;
          },
        },
      },
    },
  };

  // Calculate vulnerability levels chart data when vulnerability data changes
  const vulnerabilityLevelsChartData = (() => {
    // Create vulnerability levels array from vulnerability data
    const vulnerabilityLevels = [
      { level: "S", count: vulnerabilityData?.totalVulnerabilityLevelCountS || 0 },
      { level: "A", count: vulnerabilityData?.totalVulnerabilityLevelCountA || 0 },
      { level: "B", count: vulnerabilityData?.totalVulnerabilityLevelCountB || 0 },
      { level: "C", count: vulnerabilityData?.totalVulnerabilityLevelCountC || 0 },
    ];

    // Find max value for vulnerability levels standardization
    const maxVulnerabilityLevel = Math.max(...vulnerabilityLevels.map((l) => l.count));

    // Round up to nearest hundred or five hundred based on max value
    const standardVulnerabilityMax =
      maxVulnerabilityLevel === 0
        ? 100
        : maxVulnerabilityLevel <= 99
          ? 100
          : maxVulnerabilityLevel <= 500
            ? Math.ceil(maxVulnerabilityLevel / 500) * 500
            : Math.ceil(maxVulnerabilityLevel / 1000) * 1000;

    return {
      labels: vulnerabilityLevels.map((l) => `${l.level}`),
      datasets: [
        {
          // Active data
          data: vulnerabilityLevels.map((l) => l.count),
          backgroundColor: [colors.chartSeriesOrange, colors.chartSeriesRed, colors.chartSeriesMagenta, colors.chartSeriesBlue],
          borderWidth: 0,
          barPercentage: 1,
          categoryPercentage: 0.95,
          borderRadius: {
            topLeft: 16,
            topRight: 16,
            bottomLeft: 16,
            bottomRight: 16,
          },
        },
        {
          // Background data (remaining to reach standard)
          data: vulnerabilityLevels.map((l) => standardVulnerabilityMax - l.count),
          backgroundColor: colors.chartDonutHoleBg,
          borderWidth: 0,
          barPercentage: 1,
          categoryPercentage: 0.95,
          borderRadius: {
            topRight: 16,
            bottomRight: 16,
          },
          hoverBackgroundColor: colors.chartDonutHoleBg, // Prevent hover color change
        },
      ],
    };
  })();

  const vulnerabilityLevelsChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2,
        cornerRadius: 15, // Very rounded corners
        padding: 18, // Large padding
        // Filter tooltips at plugin level
        filter: function (tooltipItem) {
          // Only show tooltips for active data (dataset 0) with values > 0
          return tooltipItem.datasetIndex === 0 && tooltipItem.parsed.x > 0;
        },
        // Custom fonts
        bodyFont: {
          size: 14,
          weight: "500",
        },
        titleFont: {
          size: 16,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            // Only show tooltip for the first dataset (active data)
            if (context.datasetIndex === 1) return null;

            const label = context.label;
            const value = context.parsed.x;

            // Simple tooltip for vulnerability levels
            return `${label}: ${value}`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            // Only show color indicator for the first dataset
            if (context.datasetIndex === 1) return null;

            return {
              borderColor: "transparent",
              backgroundColor: context.dataset.backgroundColor[context.dataIndex],
              borderRadius: 6, // Makes it circular
            };
          },
          // Filter tooltips to only show for first dataset
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 0;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
        max: (() => {
          const vulnerabilityLevels = [
            { level: "A", count: vulnerabilityData?.totalVulnerabilityLevelCountA || 0 },
            { level: "B", count: vulnerabilityData?.totalVulnerabilityLevelCountB || 0 },
            { level: "C", count: vulnerabilityData?.totalVulnerabilityLevelCountC || 0 },
            { level: "S", count: vulnerabilityData?.totalVulnerabilityLevelCountS || 0 },
          ];
          const maxVulnerabilityLevel = Math.max(...vulnerabilityLevels.map((l) => l.count));
          return maxVulnerabilityLevel === 0
            ? 100
            : maxVulnerabilityLevel <= 99
              ? 100
              : maxVulnerabilityLevel <= 500
                ? Math.ceil(maxVulnerabilityLevel / 500) * 500
                : Math.ceil(maxVulnerabilityLevel / 1000) * 1000;
        })(),
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: colors.chartTickColor },
        border: { display: false },
      },
    },
    elements: {
      bar: {
        borderRadius: 16,
        borderSkipped: false,
      },
    },
    hover: {
      mode: "dataset",
      intersect: true,
      filter: function (e, legendItem, legend) {
        // Only allow hover on first dataset
        return legendItem.datasetIndex === 0;
      },
    },
    barThickness: 12,
  };

  // Calculate obligation types chart data when vulnerability data changes
  const obligationTypesChartData = (() => {
    // Create obligation types array from vulnerability data
    const obligationTypes = [
      { type: "1", count: vulnerabilityData?.totalObligationType1Count || 0 },
      { type: "2", count: vulnerabilityData?.totalObligationType2Count || 0 },
      { type: "3", count: vulnerabilityData?.totalObligationType3Count || 0 },
      { type: "4", count: vulnerabilityData?.totalObligationType4Count || 0 },
      { type: "5", count: vulnerabilityData?.totalObligationType5Count || 0 },
    ];

    // Find max value for obligation types standardization
    const maxObligationValue = Math.max(...obligationTypes.map((o) => o.count));

    // Round up to nearest hundred or five hundred based on max value
    const standardObligationMax =
      maxObligationValue === 0
        ? 100
        : maxObligationValue <= 99
          ? 100
          : maxObligationValue <= 500
            ? Math.ceil(maxObligationValue / 500) * 500
            : Math.ceil(maxObligationValue / 1000) * 1000;

    return {
      labels: obligationTypes.map((o) => o.type),
      datasets: [
        {
          // Active data
          data: obligationTypes.map((o) => o.count),
          backgroundColor: [colors.chartSeriesBlack, colors.chartSeriesRed, colors.chartSeriesOrange, colors.chartSeriesBlue, colors.chartSeriesGreen],
          borderWidth: 0,
          barPercentage: 1,
          categoryPercentage: 0.95,
          borderRadius: {
            topLeft: 16,
            topRight: 16,
            bottomLeft: 16,
            bottomRight: 16,
          },
        },
        {
          // Background data (remaining to reach standard)
          data: obligationTypes.map((o) => standardObligationMax - o.count),
          backgroundColor: colors.chartDonutHoleBg,
          borderWidth: 0,
          barPercentage: 1,
          categoryPercentage: 0.95,
          borderRadius: {
            topRight: 16,
            bottomRight: 16,
          },
          hoverBackgroundColor: colors.chartDonutHoleBg, // Prevent hover color change
        },
      ],
    };
  })();

  const obligationTypesChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2,
        cornerRadius: 15, // Very rounded corners
        padding: 18, // Large padding
        // Filter tooltips at plugin level
        filter: function (tooltipItem) {
          // Only show tooltips for active data (dataset 0) with values > 0
          return tooltipItem.datasetIndex === 0 && tooltipItem.parsed.x > 0;
        },
        // Custom fonts
        bodyFont: {
          size: 14,
          weight: "500",
        },
        titleFont: {
          size: 16,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            // Only show tooltip for the first dataset (active data)
            if (context.datasetIndex === 1) return null;

            const label = context.label;
            const value = context.parsed.x;
            return `${label}: ${value}`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            // Only show color indicator for the first dataset
            if (context.datasetIndex === 1) return null;

            return {
              borderColor: "transparent",
              backgroundColor: context.dataset.backgroundColor[context.dataIndex],
              borderRadius: 6, // Makes it circular
            };
          },
          // Filter tooltips to only show for first dataset
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 0;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
        max: (() => {
          const obligationTypes = [
            { type: "1", count: vulnerabilityData?.totalObligationType1Count || 0 },
            { type: "2", count: vulnerabilityData?.totalObligationType2Count || 0 },
            { type: "3", count: vulnerabilityData?.totalObligationType3Count || 0 },
            { type: "4", count: vulnerabilityData?.totalObligationType4Count || 0 },
            { type: "5", count: vulnerabilityData?.totalObligationType5Count || 0 },
          ];
          const maxObligationValue = Math.max(...obligationTypes.map((o) => o.count));
          return maxObligationValue === 0
            ? 100
            : maxObligationValue <= 99
              ? 100
              : maxObligationValue <= 500
                ? Math.ceil(maxObligationValue / 500) * 500
                : Math.ceil(maxObligationValue / 1000) * 1000;
        })(),
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: colors.chartTickColor },
        border: { display: false },
      },
    },
    elements: {
      bar: {
        borderRadius: 0,
        borderSkipped: false,
      },
    },
    hover: {
      mode: "dataset",
      intersect: true,
      filter: function (e, legendItem, legend) {
        // Only allow hover on first dataset
        return legendItem.datasetIndex === 0;
      },
    },
    barThickness: 12,
  };

  // Find max value for conflicts standardization
  const maxConflictValue = Math.max(
    vulnerabilityData.totalLicenseConflictBetweenComponentsCount,
    vulnerabilityData.totalPolicyConflictCount,
  );

  // Round up to nearest hundred or five hundred based on max value
  const standardConflictMax =
    maxConflictValue === 0
      ? 100
      : maxConflictValue <= 99
        ? 100
        : maxConflictValue <= 500
          ? Math.ceil(maxConflictValue / 500) * 500
          : Math.ceil(maxConflictValue / 1000) * 1000;

  const conflictsChartData = {
    labels: [t("dashboard.policy"), t("dashboard.license"), "", ""],
    datasets: [
      {
        // Active data
        data: [
          vulnerabilityData.totalPolicyConflictCount,
          vulnerabilityData.totalLicenseConflictBetweenComponentsCount,
          0,
          0,
        ],
        backgroundColor: [colors.chartSeriesRed, colors.chartSeriesOrange, "transparent", "transparent"],
        borderWidth: 0,
        barPercentage: 1,
        categoryPercentage: 0.95,
        borderRadius: {
          topLeft: 16,
          topRight: 16,
          bottomLeft: 16,
          bottomRight: 16,
        },
      },
      {
        // Background data (remaining to reach standard)
        data: [
          standardConflictMax - vulnerabilityData.totalPolicyConflictCount,
          standardConflictMax - vulnerabilityData.totalLicenseConflictBetweenComponentsCount,
          standardConflictMax,
          standardConflictMax,
        ],
        backgroundColor: [colors.chartDonutHoleBg, colors.chartDonutHoleBg, "transparent", "transparent"],
        borderWidth: 0,
        barPercentage: 1,
        categoryPercentage: 0.95,
        borderRadius: {
          topRight: 16,
          bottomRight: 16,
        },
        hoverBackgroundColor: [colors.chartDonutHoleBg, colors.chartDonutHoleBg, "transparent", "transparent"], // Prevent hover color change
      },
    ],
  };

  const conflictsChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: colors.chartTooltipBg,
        titleColor: colors.chartTooltipTitle,
        bodyColor: colors.chartTooltipBody,
        borderColor: colors.chartTooltipBorder,
        borderWidth: 2,
        cornerRadius: 15, // Very rounded corners
        padding: 18, // Large padding
        // Filter tooltips at plugin level
        filter: function (tooltipItem) {
          // Only show tooltips for active data (dataset 0) with values > 0
          return tooltipItem.datasetIndex === 0 && tooltipItem.parsed.x > 0;
        },
        // Custom fonts
        bodyFont: {
          size: 14,
          weight: "500",
        },
        titleFont: {
          size: 16,
          weight: "600",
        },
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            // Only show tooltip for the first dataset (active data) and non-empty values
            if (context.datasetIndex === 1 || context.parsed.x === 0) return null;

            const label = context.label;
            const value = context.parsed.x;
            return `${label}: ${value}`;
          },
          // Custom color indicator - make it circular
          labelColor: function (context) {
            // Only show color indicator for the first dataset and non-empty values
            if (context.datasetIndex === 1 || context.parsed.x === 0) return null;

            return {
              borderColor: "transparent",
              backgroundColor: context.dataset.backgroundColor[context.dataIndex],
              borderRadius: 6, // Makes it circular
            };
          },
          // Filter tooltips to only show for first dataset and non-empty values
          filter: function (tooltipItem) {
            // Only show tooltips for active data (dataset 0) with values > 0
            return tooltipItem.datasetIndex === 0 && tooltipItem.parsed.x > 0;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
        max: standardConflictMax, // Set max value to our standard
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: colors.chartTickColor },
        border: { display: false },
      },
    },
    elements: {
      bar: {
        borderRadius: 0, // Remove border radius for stacked effect
        borderSkipped: false,
      },
    },
    hover: {
      mode: "dataset",
      intersect: true,
      filter: function (e, legendItem, legend) {
        // Only allow hover on first dataset and non-empty values
        return legendItem.datasetIndex === 0 && legendItem.parsed.x > 0;
      },
    },
    barThickness: 12,
  };

  return (
    <div data-testid="dashboard-page" className={`min-h-full min-w-[960px] ${colors.bgSecondary}`}>
      {/* Header */}
      <div className="pb-5" data-testid="dashboard-header">
        <div className="space-y-3">
          <div className="flex flex-row items-center justify-between">
            <h1
              data-testid="dashboard-title"
              className={`text-2xl xl:text-[32px] 2xl:text-[36px] uppercase font-bold ${colors.textPrimary}`}
            >
              {t("dashboard.dashboard-title")}
            </h1>
            <div className="flex flex-row items-center space-x-4" data-testid="dashboard-filters">
              {/* Product Selector */}
              <div className="w-48 xl:w-56 2xl:w-64" data-testid="dashboard-product-filter">
                <DashboardProductSelect
                  data-testid="dashboard-product-select"
                  managedOptions={managedOptions}
                  participateOptions={participateOptions}
                  managedSelection={managedSelection}
                  participateSelection={participateSelection}
                  onManagedSelectionChange={setManagedSelection}
                  onParticipateSelectionChange={setParticipateSelection}
                  placeholder={t("dashboard.select-product-placeholder")}
                />
              </div>

              {/* Date Range Selector */}
              <div className="flex items-center space-x-4" data-testid="dashboard-date-filter">
                {/* Custom date range input */}
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <CustomDatePicker
                      data-testid="dashboard-date-range-picker"
                      rangeSeparator=' → '
                      selected={customDateRange.startDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setCustomDateRange({ startDate: start, endDate: end });
                        if (start && end) {
                          setDateFilter("custom");
                        } else if (!start && !end) {
                          setDateFilter(null);
                        }
                      }}
                      startDate={customDateRange.startDate}
                      endDate={customDateRange.endDate}
                      selectsRange
                      placeholderText={t("status.select-date-range")}
                      className={`h-8 w-full sm:w-48 md:w-48 xl:w-54 2xl:w-60 appearance-none rounded-lg border p-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                      dateFormat="yyyy-MM-dd"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-4 xl:space-y-6 2xl:space-y-8" data-testid="dashboard-main-content">
        {/* Row 1: Key Metrics */}
        <div className="grid grid-cols-4 gap-4 xl:gap-6 2xl:gap-8" data-testid="dashboard-metrics-row">
          {/* Total Selected Products */}
          <div
            data-testid="dashboard-metric-selected-products"
            className={`${colors.dashboardMetricCard} opacity-100 px-4 xl:px-6 2xl:px-8 py-4 xl:py-6 2xl:py-8 gap-3 rounded-xl border`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs xl:text-sm 2xl:text-base font-medium ${colors.textSubTitle}`}>
                  {t("dashboard.selected-products-count")}
                </p>
                <p className={`text-2xl xl:text-3xl 2xl:text-4xl font-bold ${colors.textPrimary} mt-2`}>
                  {totalSelectedProducts}
                </p>
              </div>
              <SelectProductIcon className="w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14" />
            </div>
          </div>

          {/* Total Projects */}
          <div
            data-testid="dashboard-metric-total-projects"
            className={`${colors.dashboardMetricCard} opacity-100 px-4 xl:px-6 2xl:px-8 py-4 xl:py-6 2xl:py-8 gap-3 rounded-xl border`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs xl:text-sm 2xl:text-base font-medium ${colors.textSubTitle}`}>
                  {t("dashboard.project-count")}
                </p>
                <p className={`text-2xl xl:text-3xl 2xl:text-4xl font-bold ${colors.textPrimary} mt-2`}>
                  {statusData.totalProjects}
                </p>
              </div>
              <TotalProjectsIcon className="w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14" />
            </div>
          </div>

          {/* Components In Use */}
          <div
            data-testid="dashboard-metric-components-in-use"
            className={`${colors.dashboardMetricCard} opacity-100 px-4 xl:px-6 2xl:px-8 py-4 xl:py-6 2xl:py-8 gap-3 rounded-xl border`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs xl:text-sm 2xl:text-base font-medium ${colors.textSubTitle}`}>
                  {t("dashboard.components-in-use")}
                </p>
                <p className={`text-2xl xl:text-3xl 2xl:text-4xl font-bold ${colors.textPrimary} mt-2`}>
                  {statusData.componentsInUse}
                </p>
              </div>
              <ComponentInUseIcon className="w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14" />
            </div>
          </div>

          {/* Components With Detected Issues */}
          <div className={`${colors.dashboardMetricCardDanger} opacity-100 px-4 xl:px-6 2xl:px-8 py-4 xl:py-6 2xl:py-8 gap-3 rounded-xl border`} data-testid="dashboard-metric-components-with-issues">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs xl:text-sm 2xl:text-base font-medium ${colors.textSubTitle}`}>
                  {t("dashboard.components-with-detected-vulnerabilities")}
                </p>
                <p className={`text-2xl xl:text-3xl 2xl:text-4xl font-bold ${colors.textPrimary} mt-2`}>
                  {statusData.componentsIssue}
                </p>
              </div>
              <ComponentIssueIcon className="w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14" />
            </div>
          </div>
        </div>

        {/* Row 2: Status and Requests */}
        <div className="grid grid-cols-4 gap-4 xl:gap-6 2xl:gap-8" data-testid="dashboard-status-requests-row">
          {/* Verification Status */}
          <div
            data-testid="dashboard-verification-status-section"
            className={`rounded-2xl lg:col-span-1 xl:col-span-1 p-4 xl:p-6 2xl:p-8 ${colors.bgPrimary} ${colors.borderPrimary} border`}
          >
            <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-8`}>
              {t("dashboard.verification-status")}
            </h3>
            <div className="h-80 xl:h-96 2xl:h-[500px] w-full min-w-0 flex items-center justify-center" data-testid="dashboard-verification-status-chart">
              {hasVerificationStatusData ? (
                <Bar
                  key={verificationChartSmallScreen ? "verification-sm" : "verification-lg"}
                  data={verificationStatusChartData}
                  options={verificationStatusChartOptions}
                />
              ) : (
                <div
                  className={`${colors.textSubTitle} text-center ${colors.bgPrimary} bg-opacity-10 flex items-center justify-center`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div
                      className={`w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 rounded-full ${colors.bgPrimary} bg-opacity-20 flex items-center justify-center`}
                    >
                      <RequestRecordIcon size={28} />
                    </div>
                    <div className="text-sm xl:text-base 2xl:text-lg font-medium">
                      {t("dashboard.no-verification-found")}
                    </div>
                    <div className="text-xs xl:text-sm 2xl:text-base opacity-70">
                      {t("dashboard.no-verification")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Your Requests */}
          <div className={`rounded-2xl col-span-3 ${colors.bgPrimary} ${colors.borderPrimary} border`} data-testid="dashboard-my-requests-section">
            <div className="flex items-center justify-between p-4 xl:p-6 2xl:p-8 pb-4">
              <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary}`}>
                {t("dashboard.my-requests-overview")}
              </h3>
            </div>

            <div className="grid grid-cols-3 gap-4 xl:gap-6 2xl:gap-8 px-4 xl:px-6 2xl:px-8 pb-4 xl:pb-6 2xl:pb-8">
              {/* Requests Overview Chart - Left Side */}
              <div className={`${colors.bgPolicyStepItem} rounded-xl p-4 xl:p-6 2xl:p-8 col-span-1`} data-testid="dashboard-requests-overview-chart">
                <h4 className={`text-sm xl:text-base 2xl:text-lg font-medium ${colors.textPrimary} mb-4 text-center`}>
                  {t("dashboard.requests-overview")}
                </h4>
                <div className="flex flex-col items-center">
                  {/* Requests Overview Chart */}
                  <div
                    className={`relative h-56 w-56 mb-4 rounded-full ${colors.dashboardChartRingBg} p-2`}
                    data-testid="dashboard-requests-doughnut-chart"
                  >
                    <Doughnut
                      style={{ position: "relative", zIndex: 2 }}
                      data={requestsChartData}
                      options={requestsChartOptions}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <div className={`text-2xl xl:text-3xl 2xl:text-4xl font-bold ${colors.textPrimary}`}>
                          {requestData.pendingApproval + requestData.pendingCompletion + requestData.pendingDone || 0}
                        </div>
                        <div className={`text-xs xl:text-sm 2xl:text-base ${colors.textSubTitle}`}>
                          {t("dashboard.total-requests")}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-col space-y-2 w-full">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${colors.dashboardLegendPurple}`}></div>
                        <span className={`text-xs xl:text-sm 2xl:text-base ${colors.textSubTitle}`}>
                          {t("dashboard.pending-completion")}
                        </span>
                      </div>
                      <span className={`${colors.textPrimary} font-medium text-xs xl:text-sm 2xl:text-base`}>
                        {requestData.pendingCompletion}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${colors.dashboardLegendPink}`}></div>
                        <span className={`text-xs xl:text-sm 2xl:text-base ${colors.textSubTitle}`}>
                          {t("dashboard.pending-approval")}
                        </span>
                      </div>
                      <span className={`${colors.textPrimary} font-medium text-xs xl:text-sm 2xl:text-base`}>
                        {requestData.pendingApproval}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${colors.dashboardLegendLime}`}></div>
                        <span className={`text-xs xl:text-sm 2xl:text-base ${colors.textSubTitle}`}>
                          {t("dashboard.done")}
                        </span>
                      </div>
                      <span className={`${colors.textPrimary} font-medium text-xs xl:text-sm 2xl:text-base`}>
                        {requestData.pendingDone}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Full Requests Table - Right Side */}
              <div data-testid="dashboard-table" className={`${colors.bgPolicyStepItem} rounded-xl p-4 xl:p-6 2xl:p-8 col-span-2 overflow-hidden`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`text-sm xl:text-base 2xl:text-lg font-semibold ${colors.textPrimary}`}>
                    {t("dashboard.my-requests-title")}
                  </h4>
                  <button
                    data-testid="dashboard-all-requests-button"
                    onClick={() => navigate("/requests")}
                    className={`${colors.textActive} ${colors.hoverTextAccentCyan} text-xs xl:text-sm 2xl:text-base font-semibold flex items-center gap-1`}
                  >
                    {t("dashboard.all-requests")}
                    <svg
                      className="w-3 h-3 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div className="w-full overflow-x-auto">
                  {/* Table Header */}
                  <div className= {`${colors.textSubTitle} min-w-[630px] font-medium text-[11px] xl:text-[13px] 2xl:text-[15px] mb-3`}>
                  <div className="grid grid-cols-[100px_150px_100px_100px_80px] gap-2 xl:gap-2 2xl:gap-4 text-[13px] xl:text-[13px] 2xl:text-[15px]">
                      <div>{t("dashboard.request-id")}</div>
                      <div>{t("dashboard.project-id")}</div>
                      <div>{t("dashboard.requester")}</div>
                      <div>{t("dashboard.assignee")}</div>
                      <div className="text-right whitespace-nowrap">{t("dashboard.completion-date")}</div>
                    </div>
                  </div>

                  {/* Table Rows */}
                  <div className="min-w-[630px] space-y-2" data-testid="dashboard-requests-list">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8" data-testid="dashboard-requests-loading">
                        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colors.borderLoader}`}></div>
                      </div>
                    ) : sortedRequests.length > 0 ? (
                      sortedRequests.slice(0, 6).map((request) => {
                        const dateInfo = formatCompletionDate(request.completionRequestDt);
                        return (
                          <div
                            key={request.projectId}
                            data-testid={`dashboard-request-row-${request.actionId || request.projectId}`}
                            onClick={() => handleRequestClick(request)}
                            title="Click to view project details"
                            className={`${colors.textPrimary} ${colors.borderPrimary} border rounded-lg ${colors.bgPrimary} bg-opacity-10 cursor-pointer hover:bg-opacity-20 transition-all duration-200 hover:border-opacity-50`}
                          >
                            <div className="grid grid-cols-[100px_150px_100px_100px_80px] gap-2 xl:gap-2 2xl:gap-4 text-[13px] xl:text-[13px] 2xl:text-[15px]">
                              <div className="font-medium pl-2 py-3 truncate" title={request.actionId}>{request.actionId}</div>
                              <div className="py-3 truncate" title={request.projectId}>
                                {request.projectId}
                              </div>
                              <div className="py-3 truncate" title={request.createrName}>
                                {request.createrName}
                              </div>
                              <div className="py-3 truncate" title={request.assigneeName}>
                                {request.assigneeName}
                              </div>
                              <div className="pr-2 py-2 text-right whitespace-nowrap">
                                <span
                                  className={`inline-block ${dateInfo.custom} ${dateInfo.color}`}
                                  title={dateInfo.text}
                                >
                                  {dateInfo.text}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div
                        className={`${colors.textSubTitle} text-center py-8 ${colors.bgPrimary} bg-opacity-10 rounded-lg border ${colors.borderPrimary} min-h-[250px] xl:min-h-[300px] 2xl:min-h-[350px] flex items-center justify-center`}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div
                            className={`w-10 h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 rounded-full ${colors.bgPrimary} bg-opacity-20 flex items-center justify-center`}
                          >
                            <RequestRecordIcon size={28} />
                          </div>
                          <div className="text-sm xl:text-base 2xl:text-lg font-medium">
                            {t("dashboard.no-requests-found")}
                          </div>
                          <div className="text-xs xl:text-sm 2xl:text-base opacity-70">
                            {t("dashboard.no-pending-requests")}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Usage and Vulnerability */}
        <div className="grid grid-cols-12 gap-4 xl:gap-6 2xl:gap-8" data-testid="dashboard-usage-vulnerability-row">
          {/* Top 5 Components/Licenses Usage */}
          <div className={`col-span-12 min-[100px]:col-span-6 rounded-2xl ${colors.bgPrimary} ${colors.borderPrimary} border flex flex-col`} data-testid="dashboard-usage-section">
            <div className="flex flex-row items-center justify-between p-4 xl:p-6 2xl:p-8 pb-4">
              <h3
                className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary}`}
              >
                {t("dashboard.top-5-components-licenses-usage")}
              </h3>
              <div className={`flex ${colors.dashboardUsageToggleBg} rounded-[100px] p-1 border ${colors.borderPrimary}`} data-testid="dashboard-usage-toggle">
                <button
                  data-testid="dashboard-usage-components-button"
                  onClick={() => setShowComponents(true)}
                  className={`px-2 sm:px-3 xl:px-4 2xl:px-5 py-1 xl:py-2 2xl:py-2 text-xs sm:text-xs xl:text-sm 2xl:text-base font-medium rounded-[100px] transition-colors ${
                    showComponents ? colors.dashboardUsageToggleActive : colors.dashboardUsageToggleInactive
                  }`}
                >
                  {t("dashboard.components")}
                </button>
                <button
                  data-testid="dashboard-usage-licenses-button"
                  onClick={() => setShowComponents(false)}
                  className={`px-2 sm:px-3 xl:px-4 2xl:px-5 py-1 xl:py-2 2xl:py-2 text-xs sm:text-xs xl:text-sm 2xl:text-base font-medium rounded-[100px] transition-colors ${
                    !showComponents ? colors.dashboardUsageToggleActive : colors.dashboardUsageToggleInactive
                  }`}
                >
                  {t("dashboard.licenses")}
                </button>
              </div>
            </div>

            <div className="flex-1 px-4 xl:px-6 2xl:px-8 pb-4 xl:pb-6 2xl:pb-8">
              <div className={`${colors.bgPolicyStepItem} rounded-xl p-4 xl:p-6 2xl:p-8 h-full`}>
                <div className="flex flex-col min-[1728px]:flex-row items-center justify-center min-[1728px]:justify-between gap-6 xl:gap-6 2xl:gap-8 h-full min-w-0">
                  {/* Chart - Left Side */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0">
                    <div
                      className={`relative h-72 w-72 xl:h-72 xl:w-72 2xl:h-80 2xl:w-80 rounded-full ${colors.dashboardChartRingBg} p-2 flex-shrink-0`}
                      data-testid="dashboard-usage-doughnut-chart"
                    >
                      <Doughnut
                        style={{ position: "relative", zIndex: 2 }}
                        data={componentsUsageChartData}
                        options={componentsUsageChartOptions}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <div
                            className={`text-4xl xl:text-4xl 2xl:text-5xl font-bold ${colors.textPrimary}`}
                          >
                            {showComponents
                              ? (statusData.topComponentsUsage || []).reduce(
                                  (sum, c) => sum + (c.componentCount || 0),
                                  0,
                                )
                              : (statusData.topLicensesUsage || []).reduce((sum, l) => sum + (l.licenseCount || 0), 0)}
                          </div>
                          <div
                            className={`text-sm xl:text-sm 2xl:text-base ${colors.textSubTitle}`}
                          >
                            {t("dashboard.total-usage")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Count List - Right Side */}
                  <div className={`w-full min-[1728px]:flex-1 min-w-0 ${colors.dashboardPanelBg} rounded-2xl p-4 xl:p-6 2xl:p-8 flex flex-col overflow-hidden`} data-testid="dashboard-usage-list">
                    <h4
                      className={`text-sm xl:text-base 2xl:text-lg text-center font-medium ${colors.textPrimary} mb-4 flex-shrink-0`}
                    >
                      {showComponents ? t("dashboard.components-usages-count") : t("dashboard.licenses-usages-count")}
                    </h4>
                    <div className="flex-1 space-y-2 xl:space-y-3 2xl:space-y-4 overflow-y-auto min-h-0">
                      {(showComponents ? statusData.topComponentsUsage || [] : statusData.topLicensesUsage || []).map(
                        (item, index) => (
                          <div
                            key={showComponents ? item.componentName : item.licenseName}
                            className={`flex items-center justify-between py-2 xl:py-2 2xl:py-2 border-b ${colors.borderMuted} last:border-b-0 flex-shrink-0`}
                          >
                            <div className="flex items-center space-x-3 xl:space-x-3 2xl:space-x-3 flex-1 min-w-0">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: componentsUsageChartData.datasets[0].backgroundColor[index] }}
                              ></div>
                              <span
                                className={`${colors.textSubTitle} text-sm xl:text-sm 2xl:text-base truncate`}
                                title={showComponents ? item.componentName : item.licenseName}
                              >
                                {showComponents ? item.componentName : item.licenseName}
                              </span>
                            </div>
                            <span
                              className={`${colors.textPrimary} font-medium text-sm xl:text-sm 2xl:text-base flex-shrink-0 ml-2`}
                            >
                              {showComponents ? item.componentCount : item.licenseCount}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Components With Detected Issues */}
          <div className={`col-span-12 min-[100px]:col-span-6 rounded-2xl ${colors.bgPrimary} ${colors.borderPrimary} border flex flex-col`} data-testid="dashboard-vulnerable-components-section">
            <div className="flex flex-row items-center justify-between p-4 xl:p-6 2xl:p-8 pb-4 mt-1 mb-2">
              <h3
                className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary}`}
              >
                {t("dashboard.vulnerable-components")}
              </h3>
              <p className={`text-xs sm:text-xs xl:text-sm 2xl:text-base ${colors.textSubTitle}`}>
                {t("dashboard.last-6-months")}
              </p>
            </div>
            <div className="flex-1 px-3 sm:px-4 xl:px-6 2xl:px-8 pb-3 sm:pb-4 xl:pb-6 2xl:pb-8 flex items-center">
              <div
                className={`h-64 sm:h-72 md:h-80 xl:h-96 2xl:h-[440px] w-full ${colors.bgPolicyStepItem} p-3 sm:p-4 xl:p-6 2xl:p-8 ${colors.borderPrimary} border rounded-2xl`}
                data-testid="dashboard-vulnerable-components-chart"
              >
                <Line data={vulnerabilityIssuesChartData} options={vulnerabilityIssuesChartOptions} />
              </div>
            </div>
          </div>
        </div>



        {/* Row 4: Vulnerability Status */}
        <div className={`${colors.bgPrimary} rounded-2xl p-4 xl:p-6 2xl:p-8 ${colors.borderPrimary} border`} data-testid="dashboard-vulnerability-status-section">
          <div className="mb-4">
            <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} uppercase`}>
              {t("dashboard.vulnerability-status")}
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-4 xl:gap-6 2xl:gap-8">
            {/* Vulnerability Level */}
            <div className={`${colors.bgPolicyStepItem} rounded-xl p-3 xl:p-4 2xl:p-6`} data-testid="dashboard-vulnerability-level-chart">
              <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-4`}>
                {t("vulnerability-policy.vulnerability-security-code")}
              </h3>
              <div className="h-40 xl:h-48 2xl:h-56 w-full relative overflow-hidden">
                <div className="w-5/6 h-full">
                  <Bar data={vulnerabilityLevelsChartData} options={vulnerabilityLevelsChartOptions} />
                </div>
                {/* Value labels on the right */}
                <div className="absolute top-2 right-2 xl:right-4 2xl:right-6 h-full flex flex-col justify-between pt-[15px] pb-[28px]">
                  {vulnerabilityLevelsChartData.datasets[0].data.map((value, index) => (
                    <div key={index} className={`text-right text-xs xl:text-sm 2xl:text-base ${colors.textNeutralLight}`}>
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Obligation Type */}
            <div className={`${colors.bgPolicyStepItem} rounded-xl p-3 xl:p-4 2xl:p-6`} data-testid="dashboard-obligation-type-chart">
              <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-4`}>
                {t("dashboard.obligation-type")}
              </h3>
              <div className="h-40 xl:h-48 2xl:h-56 w-full relative overflow-hidden">
                <div className="w-5/6 h-full">
                  <Bar data={obligationTypesChartData} options={obligationTypesChartOptions} />
                </div>
                {/* Value labels on the right */}
                <div className="absolute top-0 right-2 xl:right-4 2xl:right-6 h-full flex flex-col justify-between pt-5 pb-4">
                  {obligationTypesChartData.datasets[0].data.map((value, index) => (
                    <div key={index} className={`text-right text-xs xl:text-sm 2xl:text-base ${colors.textNeutralLight}`}>
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Conflicts */}
            <div className={`${colors.bgPolicyStepItem} rounded-xl p-3 xl:p-4 2xl:p-6`} data-testid="dashboard-conflicts-chart">
              <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-4`}>
                {t("dashboard.conflicts")}
              </h3>
              <div className="h-40 xl:h-48 2xl:h-56 w-full relative overflow-hidden">
                <div className="w-5/6 h-full">
                  <Bar data={conflictsChartData} options={conflictsChartOptions} />
                </div>
                {/* Value labels on the right - show actual values including 0 */}
                <div className="absolute top-0 right-2 xl:right-4 2xl:right-6 h-full flex flex-col justify-between py-2 pt-5.5 pb-15">
                  {[
                    vulnerabilityData.totalPolicyConflictCount,
                    vulnerabilityData.totalLicenseConflictBetweenComponentsCount,
                    null,
                    null,
                  ].map((value, index) => (
                    <div key={index} className={`text-right text-xs xl:text-sm 2xl:text-base ${colors.textNeutralLight}`}>
                      {value !== null ? value : ""}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 5: Scan Tool Status & System Information */}
        <div className="grid grid-cols-2 gap-4 xl:gap-6 2xl:gap-8" data-testid="dashboard-system-row">
          <div className={`rounded-2xl ${colors.bgPrimary} ${colors.borderPrimary} border flex flex-col`} data-testid="dashboard-scan-tool-section">
            <div className="p-3 sm:p-4 xl:p-6 2xl:p-8 pb-3 sm:pb-4 h-full flex flex-col">
            <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-4`}>
                {t("dashboard.scan-tool-status")}
              </h3>
              <div className={`${colors.bgPolicyStepItem} rounded-xl p-4 xl:p-6 2xl:p-8 space-y-4 flex-1 flex flex-col`} data-testid="dashboard-scan-tool-panel">
                {isLoadingScanTool ? (
                  <div className="flex items-center justify-center py-4 flex-1">
                    <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${colors.borderLoader}`}></div>
                  </div>
                ) : (
                  <div className="flex flex-col justify-between h-full">
                    <div className="space-y-6">
                      {/* FossID Status - Only show if enabled */}
                      {isFossIdEnabled() && (
                        <div data-testid="dashboard-scan-tool-fossid">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${getStatusColor(scanToolHealth.fossid?.status)}`}></div>
                              <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-medium`}>
                                {t("dashboard.fossid")}
                              </span>
                            </div>
                            <span className={`text-sm xl:text-base 2xl:text-lg font-semibold ${getStatusTextColor(scanToolHealth.fossid?.status)}`}>
                              {getStatusLabel(scanToolHealth.fossid)}
                            </span>
                          </div>
                          {scanToolHealth.fossid?.responseTime && (
                            <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                              <span>{t("dashboard.scan-tool-response-time")}</span>
                              <span>{scanToolHealth.fossid.responseTime}ms</span>
                            </div>
                          )}
                          {scanToolHealth.fossid?.version && (
                            <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                              <span>{t("dashboard.scan-tool-version")}</span>
                              <span>{scanToolHealth.fossid.version}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {isClarityAirEnabled() && (
                        <div data-testid="dashboard-scan-tool-clarityair">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${getStatusColor(scanToolHealth.clarityair?.status)}`}></div>
                              <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-medium`}>
                                {t("dashboard.clarityair")}
                              </span>
                            </div>
                            <span className={`text-sm xl:text-base 2xl:text-lg font-semibold ${getStatusTextColor(scanToolHealth.clarityair?.status)}`}>
                              {getStatusLabel(scanToolHealth.clarityair)}
                            </span>
                          </div>
                          {scanToolHealth.clarityair?.responseTime && (
                            <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                              <span>{t("dashboard.scan-tool-response-time")}</span>
                              <span>{scanToolHealth.clarityair.responseTime}ms</span>
                            </div>
                          )}
                          {scanToolHealth.clarityair?.version && (
                            <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                              <span>{t("dashboard.scan-tool-version")}</span>
                              <span>{scanToolHealth.clarityair.version}</span>
                            </div>
                          )}
                        </div>
                      )}                      

                      {/* Clarity Status - Always show */}
                      <div data-testid="dashboard-scan-tool-clarity">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(scanToolHealth.clarity?.status)}`}></div>
                            <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-medium`}>
                              {t("dashboard.clarity")}
                            </span>
                          </div>
                          <span className={`text-sm xl:text-base 2xl:text-lg font-semibold ${getStatusTextColor(scanToolHealth.clarity?.status)}`}>
                            {getStatusLabel(scanToolHealth.clarity)}
                          </span>
                        </div>
                        {scanToolHealth.clarity?.responseTime && (
                          <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                            <span>{t("dashboard.scan-tool-response-time")}</span>
                            <span>{scanToolHealth.clarity.responseTime}ms</span>
                          </div>
                        )}
                        {scanToolHealth.clarity?.version && (
                          <div className={`flex items-center justify-between text-xs xl:text-sm ${colors.textNeutralMuted} ml-6`}>
                            <span>{t("dashboard.scan-tool-version")}</span>
                            <span>{scanToolHealth.clarity.version}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {/* Status Summary - Always at bottom */}
                      {!isLoadingScanTool && (
                        <div className={`mt-4 pt-3 border-t ${colors.borderPrimary} border-opacity-30`} data-testid="dashboard-scan-tool-summary">
                          <div className="flex items-center justify-between text-xs xl:text-sm">
                            <span className={`${colors.textPrimary}`}>{t("dashboard.scan-tool-status-summary")}</span>
                            <div className="flex items-center space-x-2">
                              {(() => {
                                const clarityHealthy = scanToolHealth.clarity?.status === "Healthy";
                                const fossidHealthy = scanToolHealth.fossid?.status === "Healthy";
                                const clarityAirHealthy = scanToolHealth.clarityair?.status === "Healthy";
                                const fossidEnabled = isFossIdEnabled();
                                const clarityAirEnabled = isClarityAirEnabled();

                                // Clarity is always required; optional scan tools add to the check when enabled
                                const allHealthy =
                                  clarityHealthy &&
                                  (!fossidEnabled || fossidHealthy) &&
                                  (!clarityAirEnabled || clarityAirHealthy);
                                
                                return allHealthy ? (
                                  <span className={`${colors.textStatusHealthy} font-medium`}>{t("dashboard.scan-tool-all-healthy")}</span>
                                ) : (
                                  <span className={`${colors.textStatusUnconfigured} font-medium`}>{t("dashboard.scan-tool-needs-attention")}</span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* System Information */} 
          <div className={`rounded-2xl ${colors.bgPrimary} ${colors.borderPrimary} border flex flex-col`} data-testid="dashboard-system-information-section">
            <div className="p-3 sm:p-4 xl:p-6 2xl:p-8 pb-3 sm:pb-4">
              <h3 className={`text-base xl:text-lg 2xl:text-xl font-medium ${colors.textPrimary} mb-4`}>
                {t("dashboard.system-information")}
              </h3>
              <div className={`${colors.bgPolicyStepItem} rounded-xl p-4 xl:p-6 2xl:p-8 space-y-4`} data-testid="dashboard-system-information-panel">
                {isLoadingSystemInfo ? (
                  <div className="flex items-center justify-center py-8" data-testid="dashboard-system-information-loading">
                    <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${colors.borderLoader}`}></div>
                  </div>
                ) : (
                  <>
                    {/* JVM Memory */}
                    <div className="space-y-2" data-testid="dashboard-jvm-memory">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary}`}>
                            {t("dashboard.jvm-memory-usage")}
                          </span>
                          {formatUsageLabel(systemInfo.jvmMemory.available, systemInfo.jvmMemory.max) && (
                            <span className={`text-xs ${colors.textNeutralMuted}`}>
                              {formatUsageLabel(systemInfo.jvmMemory.available, systemInfo.jvmMemory.max)}
                            </span>
                          )}
                        </div>
                        <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-semibold`}>
                          {systemInfo.jvmMemory.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className={`w-full ${colors.dashboardProgressTrack} rounded-full h-2`}>
                        <div
                          className={`${colors.dashboardProgressJvm} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${Math.min(systemInfo.jvmMemory.percentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Memory Usage */}
                    <div className="space-y-2" data-testid="dashboard-memory-usage">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary}`}>
                            {t("dashboard.memory-usage")}
                          </span>
                          {formatUsageLabel(systemInfo.memoryUsage.used, systemInfo.memoryUsage.total) && (
                            <span className={`text-xs ${colors.textNeutralMuted}`}>
                              {formatUsageLabel(systemInfo.memoryUsage.used, systemInfo.memoryUsage.total)}
                            </span>
                          )}
                        </div>
                        <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-semibold`}>
                          {systemInfo.memoryUsage.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className={`w-full ${colors.dashboardProgressTrack} rounded-full h-2`}>
                        <div
                          className={`${colors.dashboardProgressMemory} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${Math.min(systemInfo.memoryUsage.percentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* CPU Usage */}
                    <div className="space-y-2" data-testid="dashboard-cpu-usage">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary}`}>
                            {t("dashboard.cpu-usage")}
                        </span>
                        <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-semibold`}>
                          {systemInfo.cpuUsage.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className={`w-full ${colors.dashboardProgressTrack} rounded-full h-2`}>
                        <div
                          className={`${colors.dashboardProgressCpu} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${Math.min(systemInfo.cpuUsage.percentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Diskspace */}
                    <div className="space-y-2" data-testid="dashboard-diskspace-usage">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary}`}>
                            {t("dashboard.diskspace-usage")}
                          </span>
                          {formatUsageLabel(systemInfo.diskspace.used, systemInfo.diskspace.total) && (
                            <span className={`text-xs ${colors.textNeutralMuted}`}>
                              {formatUsageLabel(systemInfo.diskspace.used, systemInfo.diskspace.total)}
                            </span>
                          )}
                        </div>
                        <span className={`text-sm xl:text-base 2xl:text-lg ${colors.textPrimary} font-semibold`}>
                          {systemInfo.diskspace.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className={`w-full ${colors.dashboardProgressTrack} rounded-full h-2`}>
                        <div
                          className={`${colors.dashboardProgressDisk} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${Math.min(systemInfo.diskspace.percentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Announcements Modal */}
      <AnnouncementsModal
        data-testid="dashboard-announcements-modal"
        isOpen={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        announcements={announcementsData}
      />
    </div>
  );
}

export default Dashboard;
